'use client';

import { useConversationMutations } from '@util/hooks/useConversationMutations';
import { SUPPORT_UIDS } from '@util/support';
import {
  SupportSource,
  SupportSourceToLabel,
  SupportState,
  SupportStateToLabel,
  type ChatDocument,
} from 'models/chat';

type SupportStatusBarProps = {
  chat: ChatDocument;
};

const stateOptions = [
  {
    value: SupportState.NewTicket,
    label: SupportStateToLabel[SupportState.NewTicket],
  },
  {
    value: SupportState.AwaitingCustomerReply,
    label: SupportStateToLabel[SupportState.AwaitingCustomerReply],
  },
  {
    value: SupportState.AwaitingSupportReply,
    label: SupportStateToLabel[SupportState.AwaitingSupportReply],
  },
  {
    value: SupportState.Resolved,
    label: SupportStateToLabel[SupportState.Resolved],
  },
  {
    value: SupportState.Cancelled,
    label: SupportStateToLabel[SupportState.Cancelled],
  },
];

const assigneeOptions = [
  ...Object.entries(SUPPORT_UIDS).map(([uid, { label }]) => ({
    value: uid,
    label,
  })),
  { value: 'ai-agent', label: 'AI Agent' },
];

const sourceOptions = [
  {
    value: SupportSource.PhoneSupport,
    label: SupportSourceToLabel[SupportSource.PhoneSupport],
  },
  {
    value: SupportSource.Claims,
    label: SupportSourceToLabel[SupportSource.Claims],
  },
  {
    value: SupportSource.AiSupport,
    label: SupportSourceToLabel[SupportSource.AiSupport],
  },
];

export default function SupportStatusBar({ chat }: SupportStatusBarProps) {
  const { updateSupportState, updateSupportSource, assignTicket } =
    useConversationMutations();

  return (
    <div className="flex items-center gap-6 border-t border-brand-lightest-gray px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-[1.3rem] font-medium text-zinc-500">State:</span>
        <select
          value={chat.support_state ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') return;
            updateSupportState.mutate({
              chat_id: chat.id,
              state: Number(value) as SupportState,
            });
          }}
          className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-[1.3rem] font-medium text-zinc-800 outline-none focus:border-zinc-400"
        >
          <option value="" disabled>
            Select state
          </option>
          {stateOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[1.3rem] font-medium text-zinc-500">
          Assignee:
        </span>
        <select
          value={chat.support_assigned ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') return;
            assignTicket.mutate({
              chat_id: chat.id,
              uid: value,
            });
          }}
          className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-[1.3rem] font-medium text-zinc-800 outline-none focus:border-zinc-400"
        >
          <option value="" disabled>
            Unassigned
          </option>
          {assigneeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[1.3rem] font-medium text-zinc-500">Source:</span>
        <select
          value={chat.support_source ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') return;
            updateSupportSource.mutate({
              chat_id: chat.id,
              source: value,
            });
          }}
          className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-[1.3rem] font-medium text-zinc-800 outline-none focus:border-zinc-400"
        >
          <option value="" disabled>
            Select source
          </option>
          {sourceOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
